import {
  fillHomeLoanCompareProduct,
  fillHomeLoanCompareProductSplit,
  isHomeLoanPreferredProductFixedInterestOnlyLineOfCreditOrInterestInAdvance
} from '__core/shared/util/salestrekker';
/* @ngInject */

export default (
  $q,
  $rootScope,
  $filter,
  $timeout,
  $http,
  logger,
  dataService,
  lendersService,
  HomeLoan,
  ReviewProduct,
  ReviewProductSplit,
  CompareProduct,
  CompareProductSplit,
  homeLoanDataFactory,
  FeeAndChargeType,
  helperService,
  ticketFactFindService,
  incomeService,
  expensesService,
  assetsService,
  liabilitiesService,
  HomeLoanCompliance,
  OtherMortgage,
  Product,
  ProductFee,
  OwnershipType,
  financeService,
  productsService,
  TransactionType,
  HOME_LOAN,
  DOCUMENT
) => {
  /**
   * Build current document from model.
   *
   * @param {Object} document
   *
   * @returns {Object}
   */
  const buildObject = (document) =>
    document instanceof HomeLoan ? document : HomeLoan.build(document);

  /**
   * Create Loan.
   *
   * @param {Object} document
   *
   * @returns {Object} - Created Document ID.
   */
  const createHomeLoan = (document) => {
    const deferred = $q.defer();
    logger.log('homeLoanService', 'createHomeLoan', 'Resolving DataService');
    dataService.createDocument(HomeLoan, buildObject(document)).then((id) => {
      logger.log(`homeLoanService | createHomeLoan | Resolved ${id}`);
      deferred.resolve(id);
    });
    return deferred.promise;
  };

  /**
   * Read Loan.
   *
   * @param {Object} document
   *
   * @returns {Object} - Read Document.
   */
  const readHomeLoan = (document) => {
    const deferred = $q.defer();
    logger.log('homeLoanService', 'readHomeLoan', 'Resolving DataService');
    const loan = document;
    loan.docType = HomeLoan.blank().docType;
    dataService.readDocument(HomeLoan, loan).then((data) => {
      const homeLoan = buildObject(data);
      logger.log(`homeLoanService | readHomeLoan | Resolved ${homeLoan.id}`);
      deferred.resolve(homeLoan);
    });
    return deferred.promise;
  };

  /**
   * Update Loan.
   *
   * @param {Object} document
   *
   * @returns {Object} - Has Document been Updated?
   */
  const updateHomeLoan = (document) => {
    const deferred = $q.defer();
    logger.log('homeLoanService', 'updateHomeLoan', 'Resolving DataService');
    dataService.updateDocument(HomeLoan, buildObject(document)).then((updated) => {
      logger.log(`homeLoanService | updateHomeLoan | Resolved ${updated}`);
      deferred.resolve(updated);
    });
    return deferred.promise;
  };

  /**
   * Update Loan with partial data.
   *
   * @param {Object} document
   *
   * @returns {Object} - Has Document been Updated?
   */
  const updateHomeLoanPartial = (document) => {
    const deferred = $q.defer();
    logger.log('homeLoanService', 'updateHomeLoanPartial', 'Resolving DataService');
    dataService.updateDocument(HomeLoan, document).then((updated) => {
      logger.log(`homeLoanService | updateHomeLoanPartial | Resolved ${updated}`);
      deferred.resolve(updated);
    });
    return deferred.promise;
  };

  /**
   * Delete Loan.
   *
   * @param {Object} document
   *
   * @returns {Object} - Has Document been deleted?
   */
  const deleteHomeLoan = (document) => {
    const deferred = $q.defer();
    logger.log('homeLoanService', 'deleteHomeLoan', 'Resolving DataService');
    const loan = document;
    loan.docType = HomeLoan.blank().docType;
    dataService.deleteDocument(HomeLoan, loan).then((deleted) => {
      logger.log(`homeLoanService | deleteHomeLoan | Resolved ${deleted}`);
      deferred.resolve(deleted);
    });
    return deferred.promise;
  };

  /**
   * Download calculator.
   *
   * @param {Object} [params]
   *
   * @returns {Array}
   */
  const downloadCalculator = (params) => {
    const deferred = $q.defer();
    logger.log('homeLoanService', 'downloadCalculator', 'Resolving DataFactory');
    homeLoanDataFactory.downloadCalculator(params).then((data) => {
      logger.log(`homeLoanService | downloadCalculator | Resolved ${data.length} objects`);
      deferred.resolve(data);
    });
    return deferred.promise;
  };

  /**
   * Get Astute Commission.
   *
   * @param {Object} [params]
   *
   * @returns {Array}
   */
  const astuteCommission = (params) => {
    const deferred = $q.defer();
    logger.log('homeLoanService', 'astuteCommission', 'Resolving DataFactory');
    homeLoanDataFactory.astuteCommission(params).then((data) => {
      logger.log(`homeLoanService | astuteCommission | Resolved ${data.length} objects`);
      deferred.resolve(data);
    });
    return deferred.promise;
  };

  /**
   * Get iavailable fees and charges.
   *
   * @returns {Array}
   */
  const getAvailableFeesAndCharges = () => FeeAndChargeType.available;

  /**
   * Get available applicants.
   *
   * @returns {Array}
   */
  const getAvailableApplicants = () => _.sortBy(HOME_LOAN.DETAILS.APPLICANTS, 'weight');

  /**
   * Get available repayment types.
   *
   * @returns {Array}
   */
  const getAvailableRepaymentTypes = () => {
    const vals = _.sortBy(HOME_LOAN.DETAILS.REPAYMENT_TYPE, 'weight');
    if ($rootScope.CurrentOrganization?.organization?.location?.cca2 === 'NZ') {
      return _.reject(vals, (rt) => rt.id === 'IOA');
    }
    return vals;
  };

  /**
   * Get available rate types.
   *
   * @returns {Array}
   */
  const getAvailableRateTypes = () => _.sortBy(HOME_LOAN.DETAILS.RATE_TYPE, 'weight');

  /**
   * Get available loan types.
   *
   * @returns {Array}
   */
  const getAvailableLoanTypes = () => _.sortBy(HOME_LOAN.DETAILS.LOAN_TYPE, 'weight');

  /**
   * Get available loan types.
   *
   * @returns {Array}
   */
  const getAvailablePropertyUse = () => _.sortBy(HOME_LOAN.DETAILS.PROPERTY_USE, 'weight');

  /**
   * Get yes/no types.
   *
   * @returns {Array}
   */
  const getYesNoTypes = () => _.sortBy(HOME_LOAN.DETAILS.YES_NO_TYPE, 'weight');

  /**
   * Get available offset.
   *
   * @returns {Array}
   */
  const getAvailableOffset = () => _.sortBy(HOME_LOAN.DETAILS.OFFSET, 'weight');

  /**
   * Get available employment verifacation.
   *
   * @returns {Array}
   */
  const getAvailableEmploymentVerificationMethods = () =>
    _.sortBy(HOME_LOAN.DETAILS.EMPLOYMENT_VERIFICATION_METHOD, 'weight');

  /**
   * Get ABS lending purpose codes.
   *
   * @returns {Array}
   */
  const getAvailableAbsLendingPurposeCodes = () => HOME_LOAN.DETAILS.ABS_LENDING_PURPOSE_CODES;

  /**
   * Get status of mortgages.
   *
   * @param {Array} homeLoanOtherMortgages
   * @param {String} idSecurity
   * @param {String} status
   *
   * @returns {Array}
   */
  const getStatusOfMortgages = ({ homeLoanOtherMortgages, idSecurity, status }) =>
    _.some(
      _.filter(homeLoanOtherMortgages || [], (m) => m?.idSecurity === idSecurity),
      (l) => l[status]
    );

  /**
   * Get status of mortgages of selected security.
   *
   * @param {Array} homeLoanOtherMortgages
   * @param {String} idSecurity
   * @param {Boolean} isNew
   *
   * @returns {Array}
   */
  const getStatusOfMortgagesOfSelectedSecurity = ({
    homeLoanOtherMortgages,
    idSecurity,
    isNew
  }) => {
    if (isNew) {
      return TransactionType.purchase;
    }
    if (getStatusOfMortgages({ homeLoanOtherMortgages, idSecurity, status: 'isRefinanced' })) {
      return TransactionType.refinance;
    }
    if (
      getStatusOfMortgages({
        homeLoanOtherMortgages,
        idSecurity,
        status: 'isLoanIncreased'
      })
    ) {
      return TransactionType.top_up_or_increase;
    }
    return TransactionType.equity_release;
  };

  /**
   * Find index by security id.
   *
   * @param {Array} data
   * @param {String} idSecurity
   *
   * @returns {Array}
   */
  const findIndexByIdSecurity = (data, idSecurity) =>
    _.findIndex(data, (i) => i.idSecurity === idSecurity);

  /**
   * Other security details delete.
   *
   * @param {String} idSecurity
   * @param {Array} sDetails
   * @param {Array} fWorksheets
   *
   * @returns {Array}
   */
  const otherHomeLoanSecurityDetailsDelete = (idSecurity, sDetails, fWorksheets) => {
    const securityDetails = sDetails;
    const fundingWorksheets = fWorksheets;
    if (idSecurity) {
      if (securityDetails) {
        const cVLen = securityDetails.contactsForValuation.length;
        for (let c = 0; c < cVLen; c += 1) {
          const idx = findIndexByIdSecurity(securityDetails.contactsForValuation, idSecurity);
          if (idx > -1) {
            securityDetails.contactsForValuation.splice(idx, 1);
          }
        }
        const tDLen = securityDetails.titleDetails.length;
        for (let t = 0; t < tDLen; t += 1) {
          const idx = findIndexByIdSecurity(securityDetails.titleDetails, idSecurity);
          if (idx > -1) {
            securityDetails.titleDetails.splice(idx, 1);
          }
        }
        const oMLen = securityDetails.otherMortgages.length;
        for (let o = 0; o < oMLen; o += 1) {
          const idx = findIndexByIdSecurity(securityDetails.otherMortgages, idSecurity);
          if (idx > -1) {
            securityDetails.otherMortgages.splice(idx, 1);
          }
        }
      }
      if (fundingWorksheets) {
        const fRLen = fundingWorksheets.fundsRequired.length;
        for (let r = 0; r < fRLen; r += 1) {
          const idx = findIndexByIdSecurity(fundingWorksheets.fundsRequired, idSecurity);
          if (idx > -1) {
            fundingWorksheets.fundsRequired.splice(idx, 1);
          }
        }
        const fALen = fundingWorksheets.fundsAvailable.length;
        for (let a = 0; a < fALen; a += 1) {
          const idx = findIndexByIdSecurity(fundingWorksheets.fundsAvailable, idSecurity);
          if (idx > -1) {
            fundingWorksheets.fundsAvailable.splice(idx, 1);
          } else if (!fundingWorksheets.fundsRequired.length) {
            fundingWorksheets.fundsAvailable = [];
          }
        }
        if (
          !fundingWorksheets.fundsRequired.length &&
          !fundingWorksheets.fundsAvailable.length
        ) {
          fundingWorksheets.fundsTotal.available = 0;
          fundingWorksheets.fundsTotal.difference = 0;
          fundingWorksheets.fundsTotal.lvr = 0;
          fundingWorksheets.fundsTotal.required = 0;
        }
      }
    }
    return { securityDetails, fundingWorksheets };
  };

  /**
   * Other security details delete.
   *
   * @param {Array} assets
   * @param {String} idAsset
   *
   * @returns {Object}
   */
  const getAsset = (assets, idAsset) => {
    let asset;
    if (assets && assets.length && idAsset) {
      asset = assets.find((a) => a.id === idAsset);
    }
    return asset;
  };

  /**
   * Get status of security limited option.
   *
   * @param {Array} contacts
   * @param {Array} ticketClientTypes
   * @param {Object} security
   *
   * @returns {Boolean}
   */
  const getStatusOfSecurityLimitedOption = ({ contacts, ticketClientTypes, security }) => {
    const idSecurityGuarantors = _.compact(
      _.map(
        _.filter(contacts, (c) =>
          helperService.isClientSecurityGuarantor({
            ticketClientTypes,
            idContact: c.id
          })
        ),
        'id'
      )
    );
    return (
      security?.idAsset &&
      _.some(
        security?.ownership || [],
        (s) => idSecurityGuarantors?.indexOf(s?.idContact) > -1 && s?.percent
      )
    );
  };

  /**
   * Update home loan security splits.
   *
   * @param {Array} securityDetails
   * @param {Array} fundingWorksheets
   * @param {Array} assets
   * @param {Array} contacts
   * @param {Array} ticketClientTypes
   *
   * @returns {Object}
   */
  const updateHomeLoanSecuritySplits = ({
    securityDetails,
    fundingWorksheets,
    assets,
    contacts,
    ticketClientTypes
  }) => {
    let sDetails = securityDetails;
    let fWorksheets = fundingWorksheets;
    for (let s = 0; s < sDetails.securitySplits.length; s += 1) {
      const security = sDetails.securitySplits[s];
      if (security.idAsset !== '') {
        let asset;
        if (assets.length) {
          asset = getAsset(assets, security.idAsset);
        }
        if (asset) {
          const statusOfSecurityLimitedOption = getStatusOfSecurityLimitedOption({
            contacts,
            ticketClientTypes,
            security
          });
          security.address.idLinkedAddress = asset?.address?.id || '';
          security.address.formatted = asset?.address?.formatted || '';
          security.address.placeId = asset?.address?.placeId || '';
          security.address.suiteNumber = asset?.address?.suiteNumber || '';
          security.address.streetNumber = asset?.address?.streetNumber || '';
          security.address.street = asset?.address?.street || '';
          security.address.suburb = asset?.address?.suburb || '';
          security.address.city = asset?.address?.city || '';
          security.address.state = asset?.address?.state || '';
          security.address.countryCode = asset?.address?.countryCode || '';
          security.address.country = asset?.address?.country || '';
          security.address.latitude = asset?.address?.latitude || 0;
          security.address.longitude = asset?.address?.longitude || 0;
          security.address.postCode = asset?.address?.postCode || '';
          security.address.district = asset?.address?.district || '';
          security.value = asset?.value || 0;
          security.propertyType = asset?.propertyType || 0;
          security.zoning = asset?.zoning || 0;
          if (
            (helperService.isAssetTypeOwnerOccupied(asset?.assetType || {}) &&
              !asset.willItBecomeInvestment) ||
            (helperService.isAssetTypeInvestment(asset?.assetType || {}) &&
              asset.willItBecomeOwnerOccupied)
          ) {
            security.ownershipType = OwnershipType.owner_occupied;
            security.monthlyRentalIncome = asset?.monthlyRentalIncome || 0;
            security.numberOfBoarders = asset?.numberOfBoarders || 0;
            security.allInclusive = asset?.allInclusive || false;
          } else if (
            (helperService.isAssetTypeInvestment(asset?.assetType || {}) &&
              !asset.willItBecomeOwnerOccupied) ||
            (helperService.isAssetTypeOwnerOccupied(asset?.assetType || {}) &&
              asset.willItBecomeInvestment)
          ) {
            security.ownershipType = OwnershipType.investment;
            security.monthlyRentalIncome = asset?.monthlyRentalIncome || 0;
            security.numberOfBoarders = 0;
            security.allInclusive = false;
          }
          security.toBeSold = asset?.toBeSold || false;
          security.valuation = asset?.valuation || false;
          security.ownership = asset?.ownership || [];
          if (!statusOfSecurityLimitedOption) {
            security.guaranteeAmount = 0;
            security.isLimited = false;
          }
        } else {
          const data = otherHomeLoanSecurityDetailsDelete(security.id, sDetails, fWorksheets);
          sDetails = data.securityDetails;
          fWorksheets = data.fundingWorksheets;
          sDetails.securitySplits.splice(s, 1);
        }
      }
    }
    return { sDetails, fWorksheets };
  };

  /**
   * Other security details delete.
   *
   * @param {Array} liabilities
   * @param {String} idLiability
   *
   * @returns {Object}
   */
  const getLiability = (liabilities, idLiability) => {
    let liability;
    if (liabilities && liabilities.length && idLiability) {
      liability = liabilities.find((a) => a.id === idLiability);
    }
    return liability;
  };

  /**
   * Add home loan other mortgage.
   *
   * @param {String} idSecurity
   * @param {String} idAsset
   * @param {Array} securityDetails
   * @param {Array} liabilities
   * @param {Array} contacts
   *
   * @returns {Object}
   */
  const addHomeLoanOtherMortgage = (
    idSecurity,
    idAsset,
    securityDetails,
    liabilities,
    contacts
  ) => {
    if (idSecurity && securityDetails && securityDetails.otherMortgages) {
      if (idAsset && liabilities.length) {
        const idExistingLiabilities = _.map(securityDetails.otherMortgages, 'idLiability');
        const filteredLiabilites = liabilities.filter(
          (l) => idExistingLiabilities.indexOf(l.id) === -1
        );
        const liabilitiesLinkedWithOccupiedAndInvestmentAssets = filteredLiabilites.filter(
          (l) => l.idAsset === idAsset
        );
        for (let l = 0; l < liabilitiesLinkedWithOccupiedAndInvestmentAssets.length; l += 1) {
          const otherMortgage = OtherMortgage.Blank();
          otherMortgage.idSecurity = idSecurity;
          otherMortgage.idLiability = liabilitiesLinkedWithOccupiedAndInvestmentAssets[l].id;
          otherMortgage.lender = liabilitiesLinkedWithOccupiedAndInvestmentAssets[l].name;
          otherMortgage.lenderOtherName =
            liabilitiesLinkedWithOccupiedAndInvestmentAssets[l].lenderOtherName;
          otherMortgage.bsb = liabilitiesLinkedWithOccupiedAndInvestmentAssets[l].bsb;
          otherMortgage.accountNumber =
            liabilitiesLinkedWithOccupiedAndInvestmentAssets[l].accountNumber;
          otherMortgage.interestRate =
            liabilitiesLinkedWithOccupiedAndInvestmentAssets[l].interestRate;
          otherMortgage.mortgageType =
            liabilitiesLinkedWithOccupiedAndInvestmentAssets[l].mortgageType;
          otherMortgage.limit = liabilitiesLinkedWithOccupiedAndInvestmentAssets[l].limit;
          otherMortgage.balance = liabilitiesLinkedWithOccupiedAndInvestmentAssets[l].balance;
          otherMortgage.repayment =
            liabilitiesLinkedWithOccupiedAndInvestmentAssets[l].repayment;
          otherMortgage.repaymentType =
            liabilitiesLinkedWithOccupiedAndInvestmentAssets[l].repaymentType;
          otherMortgage.ownership =
            liabilitiesLinkedWithOccupiedAndInvestmentAssets[l].ownership;
          otherMortgage.exitCosts = 0;
          otherMortgage.priority = '';
          otherMortgage.interestOnlyExpiryDate =
            liabilitiesLinkedWithOccupiedAndInvestmentAssets[l].interestOnlyExpiryDate;
          otherMortgage.fixedExpiryDate =
            liabilitiesLinkedWithOccupiedAndInvestmentAssets[l].fixedExpiryDate;
          otherMortgage.endDate = liabilitiesLinkedWithOccupiedAndInvestmentAssets[l].endDate;
          otherMortgage.isRefinanced =
            liabilitiesLinkedWithOccupiedAndInvestmentAssets[l].isRefinanced;
          otherMortgage.isLoanIncreased = false;
          otherMortgage.isTaxDeducted = false;
          securityDetails.otherMortgages.push(otherMortgage);
        }
      } else {
        const security = _.find(securityDetails.securitySplits, (s) => s.id === idSecurity);
        const otherMortgage = OtherMortgage.Blank();
        otherMortgage.idSecurity = idSecurity;
        otherMortgage.ownership = helperService.setOwnershipOfAssetOrLiability(contacts, true);
        otherMortgage.isRefinanced =
          security && security?.transactionType?.name === TransactionType.refinance.name;
        securityDetails.otherMortgages.push(otherMortgage);
      }
    }
    return securityDetails.otherMortgages;
  };

  /**
   * Get preferred product.
   *
   * @param {Array} otherMortgages
   * @param {Array} liabilities
   *
   * @returns {Array}
   */
  const updateHomeLoanOtherMortgages = (otherMortgages, liabilities) => {
    const oMortgages = otherMortgages;
    for (let i = 0; i < oMortgages.length; i += 1) {
      let liability;
      if (liabilities.length) {
        liability = getLiability(liabilities, oMortgages[i].idLiability);
      }
      if (liability) {
        oMortgages[i].lender = liability.name;
        oMortgages[i].lenderOtherName = liability.lenderOtherName;
        oMortgages[i].bsb = liability.bsb;
        oMortgages[i].accountNumber = liability.accountNumber;
        oMortgages[i].interestRate = liability.interestRate;
        oMortgages[i].mortgageType = liability.mortgageType;
        oMortgages[i].limit = liability.limit;
        oMortgages[i].balance = liability.balance;
        oMortgages[i].repayment = liability.repayment;
        oMortgages[i].repaymentType = liability.repaymentType;
        oMortgages[i].ownership = liability.ownership;
        oMortgages[i].interestOnlyExpiryDate = liability.interestOnlyExpiryDate;
        oMortgages[i].fixedExpiryDate = liability.fixedExpiryDate;
        oMortgages[i].isRefinanced = liability.isRefinanced;
      } else {
        oMortgages.splice(i, 1);
      }
    }
    return oMortgages;
  };

  /**
   * Get preferred product.
   *
   * @param {Array} products
   *
   * @returns {Object}
   */
  const getPreferredProduct = (products) => {
    let preferredProduct;
    if (products && products.length) {
      preferredProduct = products.find((p) => p.isPreferred);
    }
    return preferredProduct;
  };

  /**
   * Get funds by type.
   *
   * @param {Object} fundingWorksheets
   * @param {String} idSecurity
   * @param {String} type
   *
   * @returns {Object}
   */
  const getFundsByType = ({ fundingWorksheets, idSecurity, type }) => {
    let fundingWorksheet;
    if (fundingWorksheets && type) {
      fundingWorksheet = _.find(
        fundingWorksheets[type] || [],
        (f) => f?.idSecurity === idSecurity
      );
    }
    return fundingWorksheet;
  };

  /**
   * Add product to compare.
   *
   * @param {Object} selectedProduct
   *
   * @returns {Object}
   */
  const addProductToCompare = ({ selectedProduct }) => {
    let rP;
    let cP;
    const product = JSON.parse(JSON.stringify(selectedProduct));
    if (
      product &&
      !_.isEmpty(product?.residential) &&
      !_.isEmpty(product?.homeLoanEstimate[0]?.product)
    ) {
      const [reviewProduct, compareProduct] = fillHomeLoanCompareProduct(
        product,
        null,
        null,
        true
      );

      rP = ReviewProduct.Build(reviewProduct);
      cP = CompareProduct.Build(compareProduct);
    } else {
      rP = ReviewProduct.Blank();
      cP = CompareProduct.Blank();
    }
    return [rP, cP];
  };

  /**
   * Add product to splits.
   *
   * @param {Object} chosenProduct
   * @param {Object} searchFields
   * @param {Array} fundsAvailable
   * @param {Array} contacts
   *
   * @returns {Object}
   */
  const addProductToSplits = ({ selectedProduct }) => {
    if (!_.isEmpty(selectedProduct?.residential)) {
      const [reviewProductSplit, compareProductSplit] = fillHomeLoanCompareProductSplit(
        selectedProduct?.homeLoanEstimate[0]?.product?.productSplits[0],
        selectedProduct
      );
      return [reviewProductSplit, compareProductSplit];
    }
    return null;
  };

  /**
   * Is product IO.
   *
   * @param {Object} product
   *
   * @returns {Boolean}
   */
  const isProductIO = ({ product }) =>
    product?.selectedProduct?.residential?.repaymentType === 'IO' ||
    product?.selectedProduct?.residential?.repaymentType === 'IOA';

  /**
   * Is some risk benefit statements selected.
   *
   * @param {Object} compliance
   *
   * @returns {Boolean}
   */
  const isSomeRiskBenefitStatementsSelected = (compliance) => {
    if (helperService.getOrganizationCountry()?.cca2 === 'NZ') {
      return (
        compliance?.riskBenefitStatements?.fixedRateLoans ||
        compliance?.riskBenefitStatements?.interestOnlyLoans ||
        compliance?.riskBenefitStatements?.lineOfCredit ||
        compliance?.riskBenefitStatements?.offsetAccountFacilities ||
        compliance?.riskBenefitStatements?.variableRateLoans ||
        compliance?.riskBenefitStatements?.redraw
      );
    }
    return (
      compliance?.riskBenefitStatements?.basicVariableRateLoans ||
      compliance?.riskBenefitStatements?.fixedRateLoans ||
      compliance?.riskBenefitStatements?.interestOnlyLoans ||
      compliance?.riskBenefitStatements?.introductoryRateLoans ||
      compliance?.riskBenefitStatements?.lineOfCredit ||
      compliance?.riskBenefitStatements?.offsetAccountFacilities ||
      compliance?.riskBenefitStatements?.variableRateLoans ||
      compliance?.riskBenefitStatements?.redraw
    );
  };

  /**
   * Is preferred product fixed, interest only, line of credit, interest in advance.
   *
   * @param {Object} homeLoanCompliance
   * @param {Array} reviewProducts
   *
   * @returns {Array}
   */
  const setComplianceCommentsForFixedInterestOnlyLineOfCreditInterestInAdvance = (
    homeLoanCompliance,
    reviewProducts
  ) => {
    let compliance = HomeLoanCompliance.Blank();
    if (homeLoanCompliance && !_.isEmpty(homeLoanCompliance)) {
      const status =
        isHomeLoanPreferredProductFixedInterestOnlyLineOfCreditOrInterestInAdvance(
          reviewProducts
        );
      compliance = homeLoanCompliance;
      compliance.comments.interestOnlyProduct = status?.isInterestOnly
        ? compliance.comments.interestOnlyProduct
        : '';
      compliance.comments.fixedProduct = status?.isFixed
        ? compliance.comments.fixedProduct
        : '';
      compliance.comments.lineOfCredit = status?.isLineOfCredit
        ? compliance.comments.lineOfCredit
        : '';
      compliance.comments.interestInAdvance = status?.isInterestInAdvance
        ? compliance.comments.interestInAdvance
        : '';
    }
    return compliance;
  };

  /**
   * Get security.
   *
   * @param {Array} homeLoanSecurities
   * @param {String} idSecurity
   *
   * @returns {Object}
   */
  const getSecurity = ({ homeLoanSecurities, idSecurity }) =>
    _.find(homeLoanSecurities || [], (s) => s?.id === idSecurity);

  /**
   * Are All Required Compliance Documents Added.
   *
   * @param attachmentsCompliance
   * @param availableFileLabels
   *
   * @returns {Boolean}
   */
  const areAllRequiredComplianceDocumentsAdded = (
    attachmentsCompliance,
    availableFileLabels
  ) => {
    if (helperService.getOrganizationCountry().cca2 === 'NZ') {
      return true;
    }

    const requiredComplianceTypes = _.filter(
      _.sortBy(DOCUMENT.TYPES, 'value'),
      (type) => type.isRequired
    );
    const labels = _.uniq(_.flatten(_.map(attachmentsCompliance, 'idLabels')));
    const getLabel = (idLabel) => _.find(availableFileLabels, (label) => label.id === idLabel);

    return _.every(requiredComplianceTypes, (type) =>
      _.some(labels, (l) =>
        getLabel(l) !== undefined
          ? getLabel(l).getName().toLowerCase() === type.value.toLowerCase()
          : false
      )
    );
  };

  const splitSwitch = (params) =>
    dataService.apiRequest(null, null, true, null, $http.post, HOME_LOAN.API.SWITCH, params);

  /**
   * Set selected product.
   *
   * @param {Object} product
   * @param {Object} lender
   *
   * @returns {Object}
   */
  const setSelectedProduct = ({ product, lender }) => {
    const selectedProduct = Product.Blank();
    try {
      selectedProduct.id = product?.idProduct || '';
      selectedProduct.name = product?.productName || '';
      selectedProduct.type = 'RESIDENTIAL';
      selectedProduct.lender = [
        {
          id: lender?.id || '',
          abbr: lender?.abbr || '',
          name: lender?.name || ''
        }
      ];
      selectedProduct.residential.type = product?.isFullDoc ? 'FULL_DOC' : 'LOW_DOC';
      selectedProduct.residential.rateType = product?.isFixed ? 'FIXED' : 'VARIABLE';
      if (product?.isPrincipalAndInterest) {
        selectedProduct.residential.repaymentType = 'PI';
      }
      if (product?.isInterestOnly) {
        selectedProduct.residential.repaymentType = 'IO';
      }
      if (product?.isInterestInAdvance) {
        selectedProduct.residential.repaymentType = 'IOA';
      }
      selectedProduct.residential.propertyUse = product?.isOwnerOccupied
        ? 'OWNER_OCCUPIED'
        : 'RESIDENTIAL_INVESTMENT';
      selectedProduct.residential.initialRate = product?.interestRate || 0;
      selectedProduct.residential.revertRate = product?.revertRate || 0;
      selectedProduct.residential.fixedTerm = product?.fixedTerm || 0;
      if (product?.isSmsf !== '') {
        selectedProduct.residential.smsf = product?.isSmsf ? 'YES' : 'NO';
      }
      if (product?.isLineOfCredit !== '') {
        selectedProduct.residential.lineOfCredit = product?.isLineOfCredit ? 'YES' : 'NO';
      }
      if (product?.isConstruction !== '') {
        selectedProduct.residential.construction = product?.isConstruction ? 'YES' : 'NO';
      }
      if (product?.isLmiCapitalization !== '') {
        selectedProduct.residential.lmiCapitalization = product?.isLmiCapitalization
          ? 'YES'
          : 'NO';
      }
      if (product?.isRedrawFacility !== '') {
        selectedProduct.residential.redrawFacility = product?.isRedrawFacility ? 'YES' : 'NO';
      }
      if (product?.isAdditionalRepayments !== '') {
        selectedProduct.residential.additionalRepayments = product?.isAdditionalRepayments
          ? 'YES'
          : 'NO';
      }
      if (product?.isOffset !== '') {
        selectedProduct.residential.offset = product?.isOffset ? 'YES' : 'NO';
      }
      if (product?.isAbilityToSplitLoan !== '') {
        selectedProduct.residential.splitLoan = product?.isAbilityToSplitLoan ? 'YES' : 'NO';
      }
      if (!_.has(selectedProduct, 'residentialEstimate')) {
        selectedProduct.residentialEstimate = {};
      }
      selectedProduct.residentialEstimate.comparisonRate = product?.comparisonRate || 0;
      selectedProduct.fee = [];
      if (product?.fees?.application) {
        selectedProduct.fee.push(
          ProductFee.Build({
            type: 'APPLICATION',
            name: '',
            description: '',
            conditions: [],
            value: product?.fees?.application
          })
        );
      }
      if (product?.fees?.valuation) {
        selectedProduct.fee.push(
          ProductFee.Build({
            type: 'VALUATION',
            name: '',
            description: '',
            conditions: [],
            value: product?.fees?.valuation
          })
        );
      }
      if (product?.fees?.legal) {
        selectedProduct.fee.push(
          ProductFee.Build({
            type: 'LEGAL',
            name: '',
            description: '',
            conditions: [],
            value: product?.fees?.legal
          })
        );
      }
      if (product?.fees?.settlement) {
        selectedProduct.fee.push(
          ProductFee.Build({
            type: 'SETTLEMENT',
            name: '',
            description: '',
            conditions: [],
            value: product?.fees?.settlement
          })
        );
      }
      if (product?.fees?.packageMonthly) {
        selectedProduct.fee.push(
          ProductFee.Build({
            type: 'MONTHLY',
            name: '',
            description: '',
            conditions: [],
            value: product?.fees?.packageMonthly
          })
        );
      }
      if (product?.fees?.packageAnnual) {
        selectedProduct.fee.push(
          ProductFee.Build({
            type: 'YEARLY',
            name: '',
            description: '',
            conditions: [],
            value: product?.fees?.packageAnnual
          })
        );
      }
      if (product?.fees?.rateLock) {
        selectedProduct.fee.push(
          ProductFee.Build({
            type: 'RATE_LOCK',
            name: '',
            description: '',
            conditions: [],
            value: product?.fees?.rateLock
          })
        );
      }
      if (product?.fees?.other) {
        selectedProduct.fee.push(
          ProductFee.Build({
            type: 'OTHER',
            name: '',
            description: '',
            conditions: [],
            value: product?.fees?.other
          })
        );
      }
      selectedProduct.reward = [];
      if (product?.rewards?.hasRewards) {
        selectedProduct.reward.push({
          abbr: '',
          name: '',
          value: product?.rewards?.points || 0,
          description: product?.rewards?.description || ''
        });
      }
    } catch (ignore) {
      //
    }
    return selectedProduct;
  };

  /**
   * Set initial upfront commission.
   *
   * @param {Number} upfront
   * @param {Number} upfrontPercentage
   * @param {Number} principal
   * @param {Number} lenderUpfront
   *
   * @returns {Number}
   */
  const setCommissionUpfront = ({ upfront, upfrontPercentage, principal, lenderUpfront }) =>
    upfront !== 0 ||
    _.multiply(principal, _.multiply(lenderUpfront, _.divide(upfrontPercentage, 100)));

  /**
   * Set initial trail commission.
   *
   * @param {Number} trail
   * @param {Number} trailPercentage
   * @param {Number} principal
   * @param {Number} lenderTrail
   *
   * @returns {Number}
   */
  const setCommissionTrail = ({ trail, trailPercentage, principal, lenderTrail }) => {
    if (trail !== 0) {
      return trail;
    }
    const x = _.multiply(principal, _.multiply(lenderTrail, _.divide(trailPercentage, 100)));
    return _.divide(x, 12);
  };

  /**
   * Set initial split upfront commission.
   *
   * @param {Number} splitUpfront
   * @param {Number} splitUpfrontPercentage
   * @param {Object} parentSplit
   * @param {Boolean} isRemainder
   *
   * @returns {Number}
   */
  const setCommissionSplitUpfront = ({
    splitUpfront,
    splitUpfrontPercentage,
    parentSplit,
    isRemainder
  }) => {
    if (isRemainder !== true) {
      return splitUpfront !== 0
        ? splitUpfront
        : _.multiply(parentSplit?.upfront || 0, _.divide(splitUpfrontPercentage || 0, 100));
    }
    const total = parentSplit?.upfront || 0;
    let sum = 0;
    if (parentSplit?.splits?.length) {
      sum = _.reduce(
        parentSplit?.splits || [],
        (memo, sp) =>
          _.add(memo, _.isNumber(_.toNumber(sp?.upfront)) ? _.toNumber(sp?.upfront) : 0),
        0
      );
    }
    return _.subtract(total, sum);
  };

  /**
   * Set initial split upfront commission.
   *
   * @param {Number} splitUpfront
   * @param {Number} splitUpfrontPercentage
   * @param {Object} parentSplit
   * @param {Boolean} isRemainder
   *
   * @returns {Number}
   */
  const setCommissionSplitTrail = ({
    splitTrail,
    splitTrailPercentage,
    parentSplit,
    isRemainder
  }) => {
    if (isRemainder !== true) {
      return splitTrail !== 0
        ? splitTrail
        : _.multiply(parentSplit?.trail || 0, _.divide(splitTrailPercentage || 0, 100));
    }
    const total = parentSplit?.trail || 0;
    let sum = 0;
    if (parentSplit?.splits?.length) {
      sum = _.reduce(
        parentSplit?.splits || [],
        (memo, sp) =>
          _.add(memo, _.isNumber(_.toNumber(sp?.trail)) ? _.toNumber(sp?.trail) : 0),
        0
      );
    }
    return _.subtract(total, sum);
  };

  /**
   * Set commission.
   *
   * @param {Object} commission
   * @param {Object} preferredProduct
   * @param {Array} lenderAccreditations
   * @param {Array} commissions
   * @param {Boolean} isInitial
   *
   * @returns {Object}
   */

  const setCommission = ({
    commission,
    preferredProduct,
    lenderAccreditations,
    commissions,
    isInitial
  }) => {
    const cms = commission;
    cms.commissionSplits = [];
    let selectedCommission = {};
    if (isInitial) {
      selectedCommission = commissions?.length ? commissions[1] : {};
    } else {
      selectedCommission = _.find(commissions, (c) => c.id === cms.idCommission);
    }
    const lender = _.find(
      lenderAccreditations || [],
      (l) => l?.idLender === preferredProduct?.idLender
    );
    if (!_.isEmpty(selectedCommission) && !_.isEmpty(lender)) {
      const lenderUpfront = _.isNumber(_.toNumber(lender?.upfront))
        ? _.divide(_.toNumber(lender.upfront), 100)
        : 0;
      const lenderTrail = _.isNumber(_.toNumber(lender?.trail))
        ? _.divide(_.toNumber(lender.trail), 100)
        : 0;
      if (isInitial) {
        cms.idCommission = selectedCommission.id;
      }
      cms.commissionSplits = angular.copy(selectedCommission.splits);
      const len = cms.commissionSplits?.length || 0;
      for (let i = 0; i < len; i += 1) {
        cms.commissionSplits[i].upfront = setCommissionUpfront({
          upfront: _.isNumber(_.toNumber(cms.commissionSplits[i]?.upfront))
            ? _.toNumber(cms.commissionSplits[i].upfront)
            : 0,
          upfrontPercentage: _.isNumber(_.toNumber(cms.commissionSplits[i]?.upfrontPercentage))
            ? _.toNumber(cms.commissionSplits[i].upfrontPercentage)
            : 0,
          principal: _.isNumber(_.toNumber(preferredProduct?.costs?.principal))
            ? _.toNumber(preferredProduct?.costs?.principal)
            : 0,
          lenderUpfront
        });

        cms.commissionSplits[i].trail = setCommissionTrail({
          trail: _.isNumber(_.toNumber(cms.commissionSplits[i]?.trail))
            ? _.toNumber(cms.commissionSplits[i].trail)
            : 0,
          trailPercentage: _.isNumber(_.toNumber(cms.commissionSplits[i]?.trailPercentage))
            ? _.toNumber(cms.commissionSplits[i].trailPercentage)
            : 0,
          principal: _.isNumber(_.toNumber(preferredProduct?.costs?.principal))
            ? _.toNumber(preferredProduct?.costs?.principal)
            : 0,
          lenderTrail
        });

        const sLen = cms.commissionSplits[i]?.splits?.length || 0;
        for (let j = 0; j < sLen; j += 1) {
          cms.commissionSplits[i].splits[j].upfront = setCommissionSplitUpfront({
            splitUpfront: _.isNumber(_.toNumber(cms.commissionSplits[i]?.splits[j]?.upfront))
              ? _.toNumber(cms.commissionSplits[i].splits[j].upfront)
              : 0,
            splitUpfrontPercentage: _.isNumber(
              _.toNumber(cms.commissionSplits[i]?.splits[j]?.upfrontPercentage)
            )
              ? _.toNumber(cms.commissionSplits[i].splits[j].upfrontPercentage)
              : 0,
            parentSplit: cms.commissionSplits[i] || [],
            isRemainder: cms.commissionSplits[i]?.splits[j]?.isRemainder || false
          });
          cms.commissionSplits[i].splits[j].trail = setCommissionSplitTrail({
            splitTrail: _.isNumber(_.toNumber(cms.commissionSplits[i]?.splits[j]?.trail))
              ? _.toNumber(cms.commissionSplits[i].splits[j].trail)
              : 0,
            splitTrailPercentage: _.isNumber(
              _.toNumber(cms.commissionSplits[i]?.splits[j]?.trailPercentage)
            )
              ? _.toNumber(cms.commissionSplits[i].splits[j].trailPercentage)
              : 0,
            parentSplit: cms.commissionSplits[i] || [],
            isRemainder: cms.commissionSplits[i]?.splits[j]?.isRemainder || false
          });

          const sSLEn = cms.commissionSplits[i]?.splits[j]?.splits?.length || 0;
          for (let k = 0; k < sSLEn; k += 1) {
            cms.commissionSplits[i].splits[j].splits[k].upfront = setCommissionSplitUpfront({
              splitUpfront: _.isNumber(
                _.toNumber(cms.commissionSplits[i]?.splits[j]?.splits[k]?.upfront)
              )
                ? _.toNumber(cms.commissionSplits[i].splits[j].splits[k].upfront)
                : 0,
              splitUpfrontPercentage: _.isNumber(
                _.toNumber(cms.commissionSplits[i]?.splits[j]?.splits[k]?.upfrontPercentage)
              )
                ? _.toNumber(cms.commissionSplits[i].splits[j].splits[k].upfrontPercentage)
                : 0,
              parentSplit: cms.commissionSplits[i]?.splits[j] || [],
              isRemainder: cms.commissionSplits[i]?.splits[j]?.splits[k]?.isRemainder || false
            });
            cms.commissionSplits[i].splits[j].splits[k].trail = setCommissionSplitTrail({
              splitTrail: _.isNumber(
                _.toNumber(cms.commissionSplits[i]?.splits[j]?.splits[k]?.trail)
              )
                ? _.toNumber(cms.commissionSplits[i].splits[j].splits[k].trail)
                : 0,
              splitTrailPercentage: _.isNumber(
                _.toNumber(cms.commissionSplits[i]?.splits[j]?.splits[k]?.trailPercentage)
              )
                ? _.toNumber(cms.commissionSplits[i].splits[j].splits[k].trailPercentage)
                : 0,
              parentSplit: cms.commissionSplits[i]?.splits[j] || [],
              isRemainder: cms.commissionSplits[i]?.splits[j]?.splits[k]?.isRemainder || false
            });
          }
        }
      }
    }
    return cms;
  };

  return {
    createHomeLoan,
    readHomeLoan,
    updateHomeLoan,
    updateHomeLoanPartial,
    deleteHomeLoan,
    downloadCalculator,
    astuteCommission,
    getAvailableApplicants,
    getAvailableFeesAndCharges,
    getAvailableRepaymentTypes,
    getAvailableRateTypes,
    getAvailableLoanTypes,
    getAvailablePropertyUse,
    getYesNoTypes,
    getAvailableOffset,
    getAvailableEmploymentVerificationMethods,
    getAvailableAbsLendingPurposeCodes,
    getStatusOfMortgagesOfSelectedSecurity,
    findIndexByIdSecurity,
    otherHomeLoanSecurityDetailsDelete,
    getStatusOfSecurityLimitedOption,
    updateHomeLoanSecuritySplits,
    addHomeLoanOtherMortgage,
    updateHomeLoanOtherMortgages,
    getPreferredProduct,
    getFundsByType,
    addProductToCompare,
    addProductToSplits,
    isProductIO,
    isSomeRiskBenefitStatementsSelected,
    setComplianceCommentsForFixedInterestOnlyLineOfCreditInterestInAdvance,
    getSecurity,
    areAllRequiredComplianceDocumentsAdded,
    splitSwitch,
    setSelectedProduct,
    setCommission
  };
};
